import React, { FC, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { RARITY_HEX } from '../lib/cardState';

/**
 * The deck look for the hand (chosen 2026-10-08 over an inked and a print
 * version): cream playing cards with a thin double frame, emoji corner
 * indices, names in the serif the AOTD quotes use, a thin rarity rule, and
 * the signature as a small mark. Uses only fonts the site already ships.
 *
 * The tier shows twice: one to four diamond pips in the top-right corner
 * (readable without colour and on the smallest cards), and an inner frame
 * line in the tier colour on rare and up.
 */

export const SERIF = "'Crimson Text', Georgia, serif";
export const INK = 'rgb(35, 35, 35)';
export const DECK_INK = '#2f2a22';
export const DECK_PAPER = '#fbf7ee';
export const BRICK = '#b4532a';

/** The site-wide tier colours (styles/cardTokens.css), so a card keeps its colour from reveal to hand. */
export const RARITY_COLOR: Record<string, string> = RARITY_HEX;

/** Pips per tier, counted like suit pips. */
export const RARITY_PIPS: Record<string, number> = {
  common: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

/** The inner frame line: faint ink on commons, the tier colour above that, heavier on legendaries. */
export function frameLine(rarity: string): { color: string; width: number } {
  if (rarity === 'legendary') return { color: RARITY_COLOR.legendary, width: 2 };
  if (rarity === 'rare' || rarity === 'epic') return { color: RARITY_COLOR[rarity], width: 1.5 };
  return { color: 'rgba(47, 42, 34, 0.35)', width: 1 };
}

/** Measures an element; a callback ref, so boxes that mount later are measured too. */
export function useSize<T extends HTMLElement>(): [(el: T | null) => void, { w: number; h: number }] {
  const [el, setEl] = useState<T | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const read = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, size];
}

/** Card paper: thin outer line, a cream gap, a fainter inner line (tinted by tier when a rarity is given). */
export const paper = (shadow = true, rarity?: string): React.CSSProperties => {
  const line = frameLine(rarity ?? 'common');
  return {
    background: DECK_PAPER,
    border: `1px solid ${DECK_INK}`,
    borderRadius: 7,
    boxShadow: `inset 0 0 0 3px ${DECK_PAPER}, inset 0 0 0 ${3 + line.width}px ${line.color}${
      shadow ? ', 0 1px 2px rgba(0, 0, 0, 0.18), 0 6px 14px rgba(0, 0, 0, 0.08)' : ''
    }`,
  };
};

/** A larger panel in the same paper (card detail, prompts, the picker). */
export const Panel = styled.div`
  position: relative;
  background: ${DECK_PAPER};
  border: 1px solid ${DECK_INK};
  border-radius: 9px;
  box-shadow: inset 0 0 0 3px ${DECK_PAPER}, inset 0 0 0 4px rgba(47, 42, 34, 0.3), 0 1px 2px rgba(0, 0, 0, 0.12),
    0 6px 14px rgba(0, 0, 0, 0.06);
`;

export const Title: FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 18 }) => (
  <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: size, lineHeight: 1.15, color: DECK_INK, marginBottom: 4 }}>
    {children}
  </div>
);

export const Note: FC<{ color?: string; size?: number; style?: React.CSSProperties; children: React.ReactNode }> = ({
  color,
  size = 16,
  style,
  children,
}) => (
  <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: size, lineHeight: 1.2, color: color ?? '#7a7264', ...style }}>
    {children}
  </div>
);

/** The site's mint button, lettered in the deck serif. */
export const PlayButton = styled.button`
  border: 1.5px solid ${INK};
  box-shadow: 3px 3px rgba(0, 0, 0, 0.6);
  border-radius: 1px;
  background: #60fac1;
  padding: 4px 16px;
  font-family: ${SERIF};
  font-style: italic;
  font-weight: 600;
  font-size: 16px;
  color: ${INK};
  cursor: pointer;

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.6);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/** A small underlined pen link, for the quiet second choice next to a PlayButton. */
export const TextButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 0.75rem;
  font-style: italic;
  color: #9ca3af;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** A tappable card in a fan or a row of cards. */
export const CardSlot = styled(motion.button)`
  position: relative;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

/** The mint the PlayButton uses, for the "play now" tab on a card. */
export const MINT = '#60fac1';
